import { Inject, Injectable, NgZone, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  AiChatApiError,
  AiChatAttachmentModel,
  AiChatFeedbackCategory,
  AiChatFeedbackRating,
  AiChatMessageModel,
  AiChatStreamFrame,
  AiChatThreadModel,
  AiChatToolActivityModel,
  AiChatUsageModel
} from '@coreModels';
import { STORAGE } from '@coreConstant';
import { encodeAiChatFeedbackMessage } from '../utils/ai-chat-feedback-context';
import { cardedProducts } from '../utils/ai-chat-product-cards';
import { AiChatProductCardsService } from './ai-chat-product-cards.service';
import {
  MAX_REVEAL_UNIT_COST,
  measureRevealBacklog,
  nextRevealEnd,
  revealCost
} from '../utils/ai-chat-stream-reveal';
import { AiChatService } from './api-services/ai-chat.service';
import { ToastService } from './toast.service';
import { DebugLoggerService } from './helpers/debug-logger.service';

/**
 * What is written to storage. Only the `conversation_uuid` and the finished messages - enough
 * to put the transcript back on screen after a reload and keep the agent's own memory pointed
 * at the same conversation. Nothing transient (streaming flags, tool progress, the lockout).
 */
interface PersistedConversation {
  id: string;
  messages: AiChatMessageModel[];
}

/**
 * A reply being put on screen at a steady pace rather than as its frames land. See
 * AiChatConversationStore.revealTick().
 */
interface StreamReveal {
  /** The bubble on screen. Its `message` is always a prefix of `received`. */
  message: AiChatMessageModel;
  /** Everything the stream has delivered so far. */
  received: string;
  /** How much of `received` is on screen. */
  shown: number;
  /** The stream has closed, so nothing is still arriving. */
  isStreamDone: boolean;
  /** The current pace, in reveal-cost units (about a character each) per second. */
  rate: number;
  /** Pace earned and not yet spent - carries part of a word's cost over to the next tick. */
  budget: number;
  /** When the last tick ran, or null when the reveal is idle waiting on text. */
  lastTick: number | null;
  frameId: number | null;
  /** The end of the turn, held until the last of the reply is on screen. */
  onDrained: (() => void) | null;
}

/**
 * The one Pebbl conversation, and the turn in flight.
 *
 * WHY THIS IS A ROOT SINGLETON AND NOT COMPONENT STATE
 * ----------------------------------------------------
 * Two surfaces render this conversation - the floating widget (AiChatComponent, mounted in
 * LayoutComponent beside the router-outlet) and the full page (AskPebblComponent, a routed
 * component). When each held its own copy of the state, a turn could not survive the move
 * between them:
 *
 *   - Full page -> anywhere else. Navigation destroys AskPebblComponent, its `destroy$` fires,
 *     `takeUntil` unsubscribes and the Observable's teardown calls AbortController.abort().
 *     The reply was killed mid-sentence. Unsubscribing runs neither the `error` nor the
 *     `complete` handler, so the turn never reached finishStreamingMessage() and the partial
 *     answer was never even persisted - it was gone.
 *
 *   - Widget -> full page (the expand button). The widget survives navigation, so its stream
 *     kept running, but into state the new page could not see. The page restored from storage,
 *     which at that moment held only the question (the transcript is persisted at turn
 *     boundaries), so it showed no reply and no activity. Worse, the widget's answer landed in
 *     storage a moment later and the page's next write overwrote it.
 *
 * Holding the conversation here - alive for as long as the app is - fixes both: the stream is
 * tied to nothing that navigation can destroy, and both surfaces read the same signals, so a
 * turn started in one is already on screen in the other. Storage is now only what survives a
 * full reload, not the channel the two surfaces talk through.
 *
 * THE NO-BLANK-SCREEN RULE
 * ------------------------
 * A turn can spend many seconds gathering before it produces a single word - a product lookup
 * can chain five or more tool calls first. So the assistant bubble is created and pushed on
 * screen the instant the customer sends, and is never empty: it shows the running tool's own
 * label ("Looking up the answer"), then the reply itself as the frames arrive, then, if the turn
 * fails, the reason. There is no state in which the customer is looking at nothing.
 */
@Injectable({
  providedIn: 'root'
})
export class AiChatConversationStore {
  /* ################### Variable Declaration Start ################### */
  /** True from send until the stream closes - drives the composer's disabled state. */
  readonly isAwaitingReply = signal(false);
  /** Messages currently on screen, oldest first. */
  readonly messages = signal<AiChatMessageModel[]>([]);
  /**
   * Set when the customer's token budget is spent (429). While it holds a message the composer
   * is locked: the API refuses identically until the window resets, so retrying is pointless.
   */
  readonly rateLimitNotice = signal<string | null>(null);
  /**
   * The conversation on screen, as a signal rather than just the `conversationId` getter, so the
   * history rail can highlight the active row and re-render when it changes. A plain getter is
   * invisible to change detection in a zoneless-style signal template.
   */
  readonly activeConversationId = signal<string | null>(null);
  /**
   * True while a stored transcript is being fetched. Deliberately NOT `isAwaitingReply`: that
   * one disables the composer because a turn is in flight, and opening history is neither.
   */
  readonly isLoadingConversation = signal(false);
  /**
   * Bumped whenever something happens that the history rail's list no longer reflects - a first
   * turn in a new conversation (which is when the service creates the row and derives its
   * title), or a rename. The rail watches this and re-reads rather than the store having to
   * know the rail exists.
   */
  readonly conversationsRevision = signal(0);

  /**
   * How many messages are kept in storage. A single "what quotes does my company have" reply is
   * a wide Markdown table, so a handful of turns is already tens of kilobytes; capping the tail
   * keeps the record well inside the ~5MB origin quota without the customer ever noticing.
   */
  private static readonly MAX_PERSISTED_MESSAGES = 50;

  /* Reveal pacing, in reveal-cost units - about a character each - per second. See revealTick().
   *
   * The pace follows how much is queued, so a paragraph that lands in one frame types out over a
   * second or so instead of appearing in one jump, and a long table never trails the stream by
   * more than about REVEAL_LAG_SECONDS. The floor is a brisk reading pace, for a trickle of
   * short frames. Once the stream has closed the target tightens to REVEAL_FINAL_LAG_SECONDS:
   * nothing more is coming to smooth over, and the customer is waiting on the composer.
   *
   * Tuned against a replay of an uneven turn (a paragraph in one frame, a pause, list items, a
   * longer pause, a whole table, then the close). Shown as it landed, that turn put 360 chars on
   * screen in one quarter-second, nothing for the next 1.5s, then 474 at once; paced, it never
   * moves by more than a link or a table row at a time, and finishes about a second after the
   * stream does. */
  private static readonly REVEAL_MIN_RATE = 120;
  private static readonly REVEAL_LAG_SECONDS = 1;
  private static readonly REVEAL_FINAL_LAG_SECONDS = 0.5;
  /** How quickly the pace follows a change in what is queued. Without it a large frame snaps
   *  the speed up and the reveal lurches; with it the pace eases up and back down. */
  private static readonly REVEAL_EASING_SECONDS = 0.4;
  /** At most ~30 updates a second - each one re-renders the whole reply's Markdown. */
  private static readonly REVEAL_MIN_STEP_MS = 32;
  /** The longest gap one tick makes up for. A stall (a busy main thread) resumes at pace
   *  rather than releasing everything it missed in one jump. */
  private static readonly REVEAL_MAX_STEP_MS = 250;

  private thread: AiChatThreadModel | null = null;
  /** The reply being revealed - see revealTick(). Null between turns. */
  private reveal: StreamReveal | null = null;
  /** The turn in flight, so Stop and a re-send can cancel it. */
  private turnSubscription: Subscription | null = null;
  /** Clears the rate-limit lockout when the customer's window resets. */
  private rateLimitTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Storage is read once per app load. After that this store is authoritative, so a surface
   * opening mid-turn must never re-read and rewind what is already on screen.
   */
  private hasRestored = false;
  /* ################### Variable Declaration End ################### */

  constructor(
    private aiChatService: AiChatService,
    private toastService: ToastService,
    private debugLogger: DebugLoggerService,
    private productCardsService: AiChatProductCardsService,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  /** The active conversation_uuid, or null before the first turn. */
  get conversationId(): string | null {
    return this.thread?.id ?? null;
  }

  /**
   * Sends one turn.
   * @param message the customer's text, already trimmed
   */
  sendMessage(message: string, attachments: AiChatAttachmentModel[] = []): void {
    if (!this.canSend(message, attachments.length)) {
      return;
    }

    this.startConversationIfNeeded();
    const sent = this.aiChatService.buildMessage('user', message);
    if (attachments.length) {
      sent.attachments = attachments;
    }
    this.messages.update((current) => [...current, sent]);
    // Save the question before the reply starts: a reload mid-turn then keeps what was asked
    // rather than losing the turn entirely.
    this.persistConversation();
    this.runTurn(message, false, attachments.map((item) => item.attachment_uuid));
  }

  /**
   * Regenerates an assistant reply: drops it and asks the question again, this time flagged as
   * a regeneration so the service asks the agent for another take rather than reading it as a
   * brand new question.
   *
   * Only the newest reply can be regenerated, which is what the console allows too. The new
   * answer is appended at the end of the transcript, so regenerating something further up would
   * delete it from the middle and post its replacement at the bottom - the conversation would no
   * longer read in order.
   *
   * Note the agent is free to answer the same question differently - how it scopes a reply
   * ("my company" vs "my own") comes from the wording, not from anything we send - so a
   * regenerated answer is not guaranteed to match the one it replaced.
   */
  regenerate(message: AiChatMessageModel): void {
    if (this.isAwaitingReply() || this.rateLimitNotice() || !this.thread) {
      return;
    }

    const current = this.messages();
    const index = current.length - 1;
    // Only the last message, and only if it is the assistant reply being pointed at.
    if (index < 1 || current[index] !== message || message.role !== 'assistant') {
      return;
    }

    const question = current[index - 1];
    if (question?.role !== 'user') {
      return;
    }

    this.messages.set(current.slice(0, index));
    this.persistConversation();
    this.runTurn(question.message, true);
  }

  /**
   * Whether this message is the one Regenerate applies to - the newest reply in the transcript.
   * The surfaces use it to show the button on that message alone.
   */
  canRegenerate(message: AiChatMessageModel): boolean {
    const current = this.messages();
    return current.length > 1
      && current[current.length - 1] === message
      && message.role === 'assistant';
  }

  /**
   * Abandons the turn in flight. The request is aborted (the Observable's teardown calls
   * AbortController.abort), and whatever text had already arrived is kept - a half-answer is
   * more use than none.
   *
   * Reachable from either surface, because the subscription lives here rather than in whichever
   * component happened to start it.
   */
  stopGenerating(): void {
    if (!this.isAwaitingReply()) {
      return;
    }

    // The stream has already finished and only the reveal is still catching up. Nothing is left
    // to stop - skip to the end of the answer rather than cutting off text that has arrived.
    const reveal = this.reveal;
    if (reveal?.isStreamDone) {
      this.flushReveal();
      reveal.onDrained?.();
      return;
    }

    this.turnSubscription?.unsubscribe();
    this.turnSubscription = null;
    this.flushReveal();
    this.finishStreamingMessage((streaming) => {
      streaming.status_label = undefined;
      if (!streaming.message.trim()) {
        streaming.message = 'Stopped before Pebbl replied.';
        streaming.has_error = true;
      }
    });
  }

  /**
   * Starts over with a new `conversation_uuid`, so the agent keeps no memory of what came
   * before. Nothing to call: this channel holds no server-side thread to clear.
   *
   * THE ONLY WAY A NEW THREAD IS EVER STARTED, and reached from exactly one control: "New chat"
   * in the full page's history rail. The floating widget used to start one on every open; it
   * continues the conversation now, so this is the single point at which a customer decides they
   * are done with a thread. Both surfaces read this store, so that one click moves the widget
   * onto the new conversation too.
   *
   * It is the only thing that discards the transcript - a reload or a move to another page
   * restores it instead.
   */
  startNewConversation(): void {
    this.turnSubscription?.unsubscribe();
    this.turnSubscription = null;
    this.cancelReveal();
    this.isAwaitingReply.set(false);
    this.clearStoredConversation();
    this.thread = this.aiChatService.startThread();
    this.messages.set([]);
    this.activeConversationId.set(this.thread.id);
    // Deliberately starting over IS the restore decision for this session - see ensureRestored().
    this.hasRestored = true;
  }

  /* startFreshConversation() lived here: the floating widget called it on every open, so opening
     the bubble started a fresh enquiry rather than resuming the last one (reusing the current
     conversation when nothing had been said in it, and leaving a turn in flight alone).
     REMOVED - opening the panel now continues the conversation, and startNewConversation above is
     the only thing that ever starts one. See AiChatComponent.toggleChat for why the policy
     changed. */

  /**
   * Opens one of the customer's stored conversations, replacing what is on screen.
   *
   * The turn in flight is cancelled first. Leaving it running would let its frames land in the
   * transcript the customer has just switched to - the stream writes into whichever message
   * object it captured, and nothing about navigating away from a conversation stops it.
   *
   * The service returns the transcript oldest-first and pages by sequence number, capped at 100
   * messages per call. listAllMessages() walks the cursor to the end and hands back the whole
   * thing, so the conversation arrives complete rather than as a first page - what is wanted
   * here is the transcript, and paging it into the scroll region would mean restoring scroll
   * position on top of everything else.
   *
   * This used to ask for 200 in a single call. The service declares `limit` as le=100 and
   * answers 422 rather than clamping, so opening any stored conversation failed and left the
   * transcript empty. The bound now lives in AiChatService, where no caller can exceed it.
   *
   * `isLoadingConversation` is a separate flag from `isAwaitingReply` on purpose: the composer
   * is disabled by the latter, and a history load must not look like a turn in flight.
   *
   * @param conversationUuid the row the customer picked in the history rail
   */
  openConversation(conversationUuid: string): void {
    if (!isPlatformBrowser(this.platformId) || !conversationUuid) {
      return;
    }
    if (conversationUuid === this.activeConversationId() && !this.isLoadingConversation()) {
      return;
    }

    this.turnSubscription?.unsubscribe();
    this.turnSubscription = null;
    this.cancelReveal();
    this.isAwaitingReply.set(false);
    this.isLoadingConversation.set(true);

    // Point the thread at the chosen conversation before the messages land, so a send fired
    // while the transcript is still loading still goes to the right conversation.
    this.thread = this.aiChatService.startThread();
    this.thread.id = conversationUuid;
    this.activeConversationId.set(conversationUuid);
    this.messages.set([]);

    this.aiChatService.listAllMessages(conversationUuid).subscribe({
      next: (restored) => {
        // Guard against a slow response for a conversation the customer has already left.
        if (this.thread?.id !== conversationUuid) {
          return;
        }
        this.messages.set(restored);
        this.isLoadingConversation.set(false);
        // Make this the conversation a reload comes back to, the same as a live turn would.
        this.persistConversation();
      },
      error: (error: unknown) => {
        if (this.thread?.id !== conversationUuid) {
          return;
        }
        this.isLoadingConversation.set(false);
        this.debugLogger.error('Pebbl: failed to open conversation', error);
        this.toastService.showError('That conversation could not be opened. Please try again.');
      }
    });
  }

  /**
   * Forgets a conversation the customer has just deleted.
   *
   * Only touches what is on screen if the deleted one *is* what is on screen - deleting an old
   * thread from the rail must not disturb the conversation being had right now.
   */
  handleConversationDeleted(conversationUuid: string): void {
    if (this.activeConversationId() === conversationUuid) {
      this.startNewConversation();
    }
  }

  /**
   * Drops the conversation and everything in it (sign-out).
   */
  resetConversation(): void {
    this.turnSubscription?.unsubscribe();
    this.turnSubscription = null;
    this.cancelReveal();
    this.clearRateLimit();
    this.messages.set([]);
    this.thread = null;
    this.isAwaitingReply.set(false);
    this.isLoadingConversation.set(false);
    this.activeConversationId.set(null);
    this.clearStoredConversation();
    // Let the next sign-in read storage again rather than trusting this cleared state.
    this.hasRestored = false;
  }

  /**
   * Puts the stored conversation back on screen on the first surface to open after a load.
   *
   * Only the first call does anything: within one app session this store already holds the
   * conversation, and a second read could only rewind a turn that is still streaming into it.
   */
  ensureRestored(): void {
    if (!isPlatformBrowser(this.platformId) || this.hasRestored) {
      return;
    }
    this.hasRestored = true;

    const stored = this.readStoredConversation();
    if (!stored) {
      this.startConversationIfNeeded();
      return;
    }

    this.thread = this.aiChatService.startThread();
    // Keep the stored conversation_uuid so the agent's own memory carries on where it left off;
    // a fresh uuid would silently start the agent over with the transcript still on screen.
    this.thread.id = stored.id;
    this.activeConversationId.set(stored.id);
    this.messages.set(stored.messages);
    this.restoreAttachmentUrls();
    this.syncRestoredConversation(stored.id);
  }

  /**
   * Swaps the transcript just restored from storage for the service's own copy of it.
   *
   * Storage is only this browser's memory of the conversation, and its ids are not safe to rate
   * with. A record can be left over from an earlier login on this browser, or be older than the
   * service storing conversations at all, and a reply saved before the `complete` frame's id was
   * read carries the uuid its question was sent under. Rating any of those is a 404 ("The
   * requested conversation could not be found."). The service's transcript has the real reply
   * ids and ratings, so it replaces the stored one - the same read as opening it from history.
   *
   * A 404 means this customer has no such conversation, so it is dropped and a new one started:
   * left on screen, every rating on it would fail and every turn sent into it be refused. Any
   * other failure (offline, a 5xx, a stale token) keeps the stored transcript - it could not be
   * checked, which is not the same as being wrong.
   *
   * Skipped if the customer has moved on before the read lands - another conversation opened, a
   * new chat started, a turn sent - since swapping the transcript then would undo what they did.
   */
  private syncRestoredConversation(conversationUuid: string): void {
    const restoredCount = this.messages().length;
    const isUnchanged = () => this.thread?.id === conversationUuid
      && !this.isAwaitingReply()
      && this.messages().length === restoredCount;

    this.aiChatService.listAllMessages(conversationUuid).subscribe({
      next: (messages) => {
        if (!isUnchanged()) {
          return;
        }
        this.messages.set(messages);
        this.persistConversation();
      },
      error: (error: unknown) => {
        if (!isUnchanged()) {
          return;
        }
        if (error instanceof AiChatApiError && error.status === 404) {
          this.debugLogger.debug('Pebbl: stored conversation is not this customer\'s - starting over');
          this.startNewConversation();
          return;
        }
        this.debugLogger.debug('Pebbl: could not re-read the stored conversation', error);
      }
    });
  }

  /**
   * Trades each restored attachment's uuid for a fresh presigned view url.
   *
   * Failures are swallowed on purpose: an image whose url cannot be resolved (deleted, expired
   * bucket object, a request that just failed) simply has no url, and the template renders no
   * thumbnail for it rather than a broken one. The question text is the part that matters.
   */
  private restoreAttachmentUrls(): void {
    const pending = this.messages()
      .flatMap((message) => message.attachments ?? [])
      .filter((attachment) => !attachment.url && !!attachment.attachment_uuid);

    if (!pending.length) {
      return;
    }

    pending.forEach((attachment) => {
      this.aiChatService.resolveAttachmentUrl(attachment.attachment_uuid).subscribe({
        next: (url) => {
          if (url) {
            attachment.url = url;
            this.touchMessages();
          }
        },
        error: () => undefined
      });
    });
  }

  /**
   * Records a thumbs rating on a reply, and tells the service about it.
   *
   * Re-clicking the lit thumb clears it, which is the behaviour every other chat has.
   *
   * The thumb is set optimistically and reverted if the call fails, so the button responds
   * instantly but never ends up showing a rating the service does not hold. It is also kept in
   * the persisted transcript, so the lit thumb is back on screen the moment a reload restores it,
   * before syncRestoredConversation re-reads the ratings from the service.
   *
   * A reply with no `message_uuid` (stopped before the service sent its id, or refused) has
   * nothing to rate against, so it is left local-only rather than firing a call the service
   * would reject. Both ids must be the service's own: the open conversation, and the reply's id
   * from the `complete` frame or the stored transcript - see applyFrame and
   * syncRestoredConversation.
   */
  vote(message: AiChatMessageModel, choice: AiChatFeedbackRating): void {
    const previous = message.feedback ?? null;
    const next = previous === choice ? null : choice;

    message.feedback = next;
    this.touchMessages();
    this.persistConversation();

    const conversationUuid = this.thread?.id;
    if (!conversationUuid || !message.message_uuid) {
      return;
    }

    const request$ = next === null
      ? this.aiChatService.clearFeedback(conversationUuid, message.message_uuid)
      : this.aiChatService.setFeedback(conversationUuid, message.message_uuid, next);

    request$.subscribe({
      error: (error: unknown) => {
        message.feedback = previous;
        this.touchMessages();
        this.persistConversation();
        this.dropStaleTokenOn401(error);
        this.logFeedbackFailure('rating', error);
      }
    });
  }

  /**
   * Files the thumbs-down detail the customer just filled in, and lights the thumb.
   *
   * The thumb is set optimistically and kept even if the post fails: the customer told us the
   * reply was bad, and quietly un-setting it because a network call failed would look like the
   * click did not register. The failure is surfaced as a toast instead.
   *
   * @param message the reply being rated
   * @param category one of AI_CHAT_FEEDBACK_CATEGORIES
   * @param note the customer's free-text note, already trimmed
   */
  submitFeedback(
    message: AiChatMessageModel,
    category: AiChatFeedbackCategory,
    note: string
  ): void {
    message.feedback = 'down';
    this.touchMessages();
    this.persistConversation();

    // The API stores only a free-text body, so the conversation and the rated Q&A are encoded
    // into it - the same footer the console writes, so its review section can read both.
    const body = encodeAiChatFeedbackMessage(note, {
      conversationId: this.thread?.id,
      question: this.questionFor(message),
      answer: message.message
    });

    this.aiChatService.createFeedback(category, body).subscribe({
      next: () => this.toastService.showSuccess('Thanks - your feedback has been sent.'),
      error: (error: unknown) => {
        this.logFeedbackFailure('submission', error);
        this.toastService.showError('Your feedback could not be sent. Please try again.');
      }
    });

    // The rating is a separate call from the review item, exactly as in the console, and the two
    // are independent: the note is filed even if the thumb fails to stick, and clearing the thumb
    // later does not retract the note.
    const conversationUuid = this.thread?.id;
    if (conversationUuid && message.message_uuid) {
      this.aiChatService.setFeedback(conversationUuid, message.message_uuid, 'down').subscribe({
        error: (error: unknown) => {
          this.dropStaleTokenOn401(error);
          this.logFeedbackFailure('rating', error);
        }
      });
    }
  }

  /**
   * Drops the cached service token on a 401, same as handleTurnError: the token is short-lived
   * and cached in memory with no expiry check, so once it goes stale server-side every AI-service
   * call 401s until something clears it. Without this, a stale token made every thumbs up/down
   * fail silently and forever - the next vote re-fetches a fresh token instead.
   */
  private dropStaleTokenOn401(error: unknown): void {
    if (error instanceof AiChatApiError && error.status === 401) {
      this.aiChatService.invalidateToken();
    }
  }

  /** One place for the feedback calls' failure logging, which all want the same three fields. */
  private logFeedbackFailure(what: string, error: unknown): void {
    const apiError = error instanceof AiChatApiError ? error : null;
    this.debugLogger.error(
      `Pebbl feedback ${what} failed - status=${apiError?.status ?? 0}`,
      `request_uuid=${apiError?.requestUuid ?? 'none'}`,
      apiError?.apiMessage ?? 'unknown'
    );
  }

  /** The question a reply answered - the nearest user message before it. */
  private questionFor(message: AiChatMessageModel): string {
    const current = this.messages();
    for (let index = current.indexOf(message) - 1; index >= 0; index--) {
      if (current[index].role === 'user') {
        return current[index].message;
      }
    }
    return '';
  }

  /**
   * Whether a message may be sent right now.
   */
  canSend(message: string, attachmentCount = 0): boolean {
    // An image-only turn is valid - the API accepts one with no text, and "what is this stone?"
    // over a photo is the obvious use for attachments here.
    return (!!message || attachmentCount > 0)
      && !this.isAwaitingReply()
      && !this.rateLimitNotice();
  }

  /**
   * Ensures a conversation exists, and exposes its id.
   *
   * Public because an image upload has to name the conversation it belongs to, and the customer
   * can attach one before typing a word - so the id must exist before the first turn is sent.
   */
  ensureConversation(): void {
    this.startConversationIfNeeded();
  }

  /**
   * Ensures a conversation exists. Cheap and synchronous - no thread endpoint to call - so the
   * panel opens straight into the greeting with no loading flash.
   */
  private startConversationIfNeeded(): void {
    if (this.thread) {
      return;
    }
    this.thread = this.aiChatService.startThread();
    this.activeConversationId.set(this.thread.id);
  }

  /**
   * Runs one turn: places the assistant bubble, then folds each frame into it as it arrives.
   *
   * Deliberately NOT piped through `takeUntil(destroy$)`. The turn belongs to the conversation,
   * not to whichever component started it, so navigating away - or expanding the widget into
   * the full page - leaves it running. Cancelling is Stop, Clear Chat or sign-out, all of which
   * unsubscribe explicitly.
   *
   * @param regenerate marks the turn as another take on the question already answered.
   */
  private runTurn(message: string, regenerate = false, attachmentUuids: string[] = []): void {
    this.startConversationIfNeeded();

    const streaming = this.aiChatService.buildMessage('assistant', '');
    streaming.is_streaming = true;
    streaming.tool_activity = [];
    streaming.status_label = 'Pebbl is thinking';
    // Fresh per turn, including on a regeneration: the service keys the stored answer on this,
    // so re-using the previous turn's uuid would overwrite that answer instead of adding one.
    // It names the turn being sent, NOT the reply - the reply's own id only arrives on the
    // `complete` frame (see applyFrame), and rating a reply with this one is a 404.
    const turnUuid = this.aiChatService.createMessageUuid();

    this.messages.update((current) => [...current, streaming]);
    this.isAwaitingReply.set(true);
    this.startReveal(streaming);

    this.turnSubscription = this.aiChatService
      .streamMessage(this.thread!.id, turnUuid, message, regenerate, attachmentUuids)
      .subscribe({
        next: (frame: AiChatStreamFrame) => this.applyFrame(streaming, frame),
        error: (error: unknown) => this.handleTurnError(streaming, error),
        // The stream is done, but the reveal can still be a beat behind it. The turn only ends
        // once the last of the reply is on screen - the caret, the actions row and the composer
        // all key off that, and appearing under a reply still typing out would read as broken.
        complete: () => this.endReveal(() => {
          this.finishStreamingMessage((entry) => {
            entry.status_label = undefined;
            // A stream that closes without a word still has to say something.
            if (!entry.message.trim() && !entry.has_error) {
              entry.message = 'Pebbl did not have an answer for that. Try rephrasing the question.';
              entry.has_error = true;
            }
          });
          // The service creates the history row on the first turn and derives its title from
          // the opening question, so the rail is out of date the moment a new conversation
          // completes its first exchange. Announced on every turn, not just the first: the
          // ordering is by `updated_at`, so a reply to an old thread moves it to the top too.
          this.conversationsRevision.update((value) => value + 1);
        })
      });
  }

  /* ################### Reveal Pacing Start ################### */

  /**
   * Starts revealing the reply that `message` will hold.
   *
   * WHY THE REPLY IS NOT SHOWN AS IT ARRIVES
   * ----------------------------------------
   * The agent's frames are uneven - a single table row, then a whole paragraph after a pause
   * while a tool ran - and written straight into the bubble, short lines ticked out while long
   * paragraphs appeared in one jump. So `answer` text is queued here instead, and revealTick()
   * moves it on screen a word at a time at a pace that follows the queue. What counts as a word,
   * and why a link or a table row is never cut part-way, is in core/utils/ai-chat-stream-reveal.
   */
  private startReveal(message: AiChatMessageModel): void {
    this.cancelReveal();
    this.reveal = {
      message,
      received: '',
      shown: 0,
      isStreamDone: false,
      rate: AiChatConversationStore.REVEAL_MIN_RATE,
      // A full unit's worth up front, so the first word lands on the first tick.
      budget: MAX_REVEAL_UNIT_COST,
      lastTick: null,
      frameId: null,
      onDrained: null
    };
  }

  /** Queues answer text for the reveal. */
  private queueRevealText(message: AiChatMessageModel, text: string): void {
    const reveal = this.reveal;
    if (!reveal || reveal.message !== message || !text) {
      return;
    }
    reveal.received += text;
    this.scheduleRevealTick();
  }

  /** The text the stream has delivered for `message`, revealed or not. */
  private receivedText(message: AiChatMessageModel): string {
    return this.reveal?.message === message ? this.reveal.received : message.message;
  }

  /**
   * Marks the stream closed. The rest of the reply keeps revealing at the same pace, and
   * `onDrained` - the end of the turn - runs once the last of it is on screen.
   */
  private endReveal(onDrained: () => void): void {
    const reveal = this.reveal;
    if (!reveal) {
      onDrained();
      return;
    }
    reveal.isStreamDone = true;
    reveal.onDrained = onDrained;
    this.scheduleRevealTick();
  }

  /**
   * Puts everything received on screen at once and stops revealing. For a turn that is ending
   * early - refused, failed or stopped - where whatever has arrived is shown as it stands.
   */
  private flushReveal(): void {
    const reveal = this.reveal;
    if (!reveal) {
      return;
    }
    this.cancelReveal();
    if (reveal.received.length > reveal.message.message.length) {
      reveal.message.message = reveal.received;
      reveal.message.status_label = undefined;
    }
  }

  /** Drops the reveal without showing any more of it. */
  private cancelReveal(): void {
    if (this.reveal?.frameId != null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.reveal.frameId);
    }
    this.reveal = null;
  }

  /**
   * Asks for the next reveal tick, unless one is already pending.
   *
   * Animation frames run outside Angular: most ticks change nothing on screen (the pace has not
   * earned a whole word yet), and inside the zone each one would still run change detection over
   * the whole app. revealTick() re-enters the zone only for the ticks that show something.
   */
  private scheduleRevealTick(): void {
    const reveal = this.reveal;
    if (!reveal) {
      return;
    }

    // No animation frames on the server, and none at all in a background tab - the browser
    // pauses them there, including one already requested, which would hold the reply (and the
    // end of the turn) until the customer came back. Nobody is watching either way, so show
    // the text as it lands.
    if (typeof requestAnimationFrame !== 'function' || document.hidden) {
      if (reveal.frameId !== null) {
        cancelAnimationFrame(reveal.frameId);
        reveal.frameId = null;
      }
      this.ngZone.run(() => this.revealAll(reveal));
      return;
    }

    if (reveal.frameId !== null) {
      return;
    }
    reveal.frameId = this.ngZone.runOutsideAngular(() =>
      requestAnimationFrame((now) => this.revealTick(reveal, now)));
  }

  /**
   * One step of the reveal.
   *
   * THE PACE
   * --------
   * The pace aims to have whatever is queued on screen within REVEAL_LAG_SECONDS (the final lag
   * once the stream has closed), never slower than REVEAL_MIN_RATE, and eases towards that
   * target rather than jumping to it. That is what
   * makes the uneven frames read as one even stream: a big frame speeds the reveal up gently
   * instead of dumping a paragraph, and a pause in the frames slows it down gently instead of
   * stopping it dead. Each tick earns `rate x elapsed` of budget and spends it on whole units;
   * a unit that costs more than is left waits for the next tick.
   */
  private revealTick(reveal: StreamReveal, now: number): void {
    if (this.reveal !== reveal) {
      return;
    }
    reveal.frameId = null;

    const sinceLast = reveal.lastTick === null
      ? AiChatConversationStore.REVEAL_MIN_STEP_MS
      : now - reveal.lastTick;
    if (sinceLast < AiChatConversationStore.REVEAL_MIN_STEP_MS) {
      this.scheduleRevealTick();
      return;
    }
    reveal.lastTick = now;
    const seconds = Math.min(sinceLast, AiChatConversationStore.REVEAL_MAX_STEP_MS) / 1000;

    const backlog = measureRevealBacklog(reveal.received, reveal.shown, reveal.isStreamDone);
    const lag = reveal.isStreamDone
      ? AiChatConversationStore.REVEAL_FINAL_LAG_SECONDS
      : AiChatConversationStore.REVEAL_LAG_SECONDS;
    const target = Math.max(AiChatConversationStore.REVEAL_MIN_RATE, backlog / lag);
    reveal.rate += (target - reveal.rate)
      * (1 - Math.exp(-seconds / AiChatConversationStore.REVEAL_EASING_SECONDS));
    reveal.budget += reveal.rate * seconds;

    let shown = reveal.shown;
    while (shown < reveal.received.length) {
      const end = nextRevealEnd(reveal.received, shown, reveal.isStreamDone);
      const cost = revealCost(shown, end);
      // Either the next unit is still arriving, or this tick cannot afford it yet.
      if (end <= shown || cost > reveal.budget) {
        break;
      }
      reveal.budget -= cost;
      shown = end;
    }
    // Budget is never banked past one unit. Pace earned while waiting on text would otherwise
    // be released as a burst the moment the text arrived - the very jump this exists to remove.
    reveal.budget = Math.min(reveal.budget, MAX_REVEAL_UNIT_COST);

    if (shown !== reveal.shown) {
      reveal.shown = shown;
      this.ngZone.run(() => this.showRevealed(reveal));
    }

    if (reveal.isStreamDone && reveal.shown >= reveal.received.length) {
      this.ngZone.run(() => this.completeReveal(reveal));
      return;
    }

    const hasReadyUnit = reveal.shown < reveal.received.length
      && nextRevealEnd(reveal.received, reveal.shown, reveal.isStreamDone) > reveal.shown;
    if (hasReadyUnit) {
      this.scheduleRevealTick();
    } else {
      // Caught up, or waiting on a unit still arriving. Idle until the next frame queues text
      // (queueRevealText reschedules), and restart the clock then rather than counting the wait.
      reveal.lastTick = null;
    }
  }

  /** Shows everything received, and ends the turn if the stream has closed. */
  private revealAll(reveal: StreamReveal): void {
    reveal.shown = reveal.received.length;
    this.showRevealed(reveal);
    if (reveal.isStreamDone) {
      this.completeReveal(reveal);
    }
  }

  /** Writes the revealed part of the reply into the bubble. */
  private showRevealed(reveal: StreamReveal): void {
    reveal.message.message = reveal.received.slice(0, reveal.shown);
    // The status line ("Pebbl is thinking") stays up until there are words to replace it - so
    // the bubble is never empty between the first frame landing and the first word showing.
    if (reveal.message.message.trim()) {
      reveal.message.status_label = undefined;
    }
    this.touchMessages();
  }

  /** The last of the reply is on screen: end the turn. */
  private completeReveal(reveal: StreamReveal): void {
    if (this.reveal === reveal) {
      this.reveal = null;
    }
    reveal.onDrained?.();
  }

  /* ################### Reveal Pacing End ################### */

  /**
   * Folds one frame into the streaming message.
   *
   * Unrecognised types are ignored on purpose: the guide warns that a model swap could start
   * emitting frames this channel has not sent before, and an unknown frame is not an error.
   */
  private applyFrame(streaming: AiChatMessageModel, frame: AiChatStreamFrame): void {
    switch (frame.type) {
      case 'tool_start': {
        const activity = new AiChatToolActivityModel();
        activity.id = String(frame.data['id'] ?? '');
        activity.name = String(frame.data['name'] ?? '');
        activity.label = String(frame.data['label'] ?? '').trim() || 'Looking that up';
        streaming.tool_activity = [...(streaming.tool_activity ?? []), activity];
        // Only tell the customer what is happening while there is no answer text yet;
        // once text is streaming, a changing status line under it is just noise.
        if (!this.receivedText(streaming)) {
          streaming.status_label = activity.label;
        }
        break;
      }

      case 'tool_end': {
        // Matched on id: tools can run in sequence, several per turn.
        const id = String(frame.data['id'] ?? '');
        const activity = (streaming.tool_activity ?? []).find((entry) => entry.id === id);
        if (activity) {
          activity.is_complete = true;
          activity.summary = String(frame.data['summary'] ?? '');
        }
        break;
      }

      case 'answer': {
        // Concatenate in arrival order - a table can arrive as one frame per row, and no single
        // frame is ever the whole reply. Queued, not written into the bubble: the reveal puts it
        // on screen at an even pace (see startReveal), and clears the status line when it does.
        this.queueRevealText(streaming, String(frame.data['text'] ?? ''));
        break;
      }

      case 'component': {
        // Product cards: at most one frame per reply, after the last answer text and before
        // usage. The text is already on screen; the entries pick up their pictures when this
        // lands. A second frame still appends rather than replacing; an empty or unknown
        // component is dropped by cardedProducts.
        const products = cardedProducts([frame.data]);
        this.debugLogger.debug('Pebbl component frame', frame.data);
        if (products.length) {
          const skus = products.map((product) => product.sku);
          streaming.product_skus = [...(streaming.product_skus ?? []), ...skus];
          streaming.product_links = [...(streaming.product_links ?? []), ...products.map((product) => product.link)];
          streaming.product_recommended = [
            ...(streaming.product_recommended ?? []), ...products.map((product) => product.recommended)
          ];
          this.productCardsService.prefetch(skus);
        }
        break;
      }

      case 'usage': {
        streaming.usage = frame.data as AiChatUsageModel;
        // debug(), not log() - DebugLoggerService.log() has an empty body and prints nothing.
        this.debugLogger.debug('Pebbl turn usage', frame.data);
        break;
      }

      case 'complete': {
        // Terminal frame. Its `message_uuid` is the service's id for the reply it has just
        // stored, and the only id a rating on this reply is accepted against. Null when the
        // turn was refused and nothing was stored - the reply then has no id to rate. The
        // Observable completes on its own when the body closes.
        const replyUuid = frame.data['message_uuid'];
        if (typeof replyUuid === 'string' && replyUuid) {
          streaming.message_uuid = replyUuid;
        }
        break;
      }

      default:
        // Logged so a frame shape this channel has not sent before is visible in debug builds
        // rather than vanishing silently.
        this.debugLogger.debug('Pebbl ignored frame', frame.type, frame.data);
        break;
    }

    this.touchMessages();
  }

  /**
   * Turns a refusal or a network failure into something the customer can act on, written into
   * the assistant bubble that is already on screen.
   */
  private handleTurnError(streaming: AiChatMessageModel, error: unknown): void {
    // Whatever arrived before the failure is shown in full, with the reason under it.
    this.flushReveal();

    const apiError = error instanceof AiChatApiError
      ? error
      : new AiChatApiError({ status: 0, apiMessage: 'We could not reach the assistant. Please try again.' });

    // Always logged, including in production (DebugLoggerService.error does not gate on
    // debugMode). request_uuid is the identifier the API team asks for when reporting a
    // problem, and a server-side failure can arrive without one - log the status either way.
    this.debugLogger.error(
      `Pebbl chat turn failed - status=${apiError.status}`,
      `request_uuid=${apiError.requestUuid ?? 'none'}`,
      apiError.apiMessage
    );

    let notice: string;

    switch (apiError.status) {
      case 401:
        // The service's own 401 copy ("Token is required", "Invalid client ID.") is not for
        // customers. The cached token may simply be stale while the auth cookie is still good,
        // so drop it - the next send re-fetches before deciding the session is really over.
        this.dropStaleTokenOn401(apiError);
        notice = 'Your session has expired. Please sign in again to keep chatting.';
        this.toastService.showError(notice);
        break;

      case 403:
        notice = apiError.apiMessage;
        break;

      case 429:
        notice = this.applyRateLimit(apiError);
        break;

      case 422:
        // A bad body is our bug, not the customer's - say something they can act on.
        notice = 'That message could not be sent. Please shorten it and try again.';
        break;

      default:
        // A 5xx message describes a server fault and can name internals - a real staging 500
        // carried "JWT_PUBLIC_KEY_WEBSITE is not configured". Never surface it; the real
        // detail goes to the console log above, for the API team.
        notice = apiError.status >= 500
          ? 'Pebbl is having trouble at our end right now. Please try again shortly.'
          : apiError.apiMessage;
        break;
    }

    this.finishStreamingMessage((entry) => {
      entry.status_label = undefined;
      entry.has_error = true;
      // Keep any partial answer and append the reason underneath it.
      entry.message = entry.message.trim() ? `${entry.message.trim()}\n\n_${notice}_` : notice;
    });
  }

  /**
   * Locks the composer until the customer's token budget resets.
   * @returns the notice to show in the bubble as well
   */
  private applyRateLimit(apiError: AiChatApiError): string {
    const resetsAt = apiError.retryAt
      ? new Date(apiError.retryAt)
      : apiError.retryAfterSeconds
        ? new Date(Date.now() + apiError.retryAfterSeconds * 1000)
        : null;

    const notice = resetsAt && !isNaN(resetsAt.getTime())
      // Rendered in the customer's own timezone; the API reports UTC.
      ? `${apiError.apiMessage} You can chat again from ${this.formatResetTime(resetsAt)}.`
      : apiError.apiMessage;

    this.rateLimitNotice.set(notice);

    // Lift the lock by itself when the window passes, so a customer who leaves the tab open is
    // not stuck behind a stale message.
    if (isPlatformBrowser(this.platformId) && resetsAt && !isNaN(resetsAt.getTime())) {
      const wait = resetsAt.getTime() - Date.now();
      if (wait > 0) {
        this.clearRateLimitTimer();
        this.rateLimitTimer = setTimeout(() => this.clearRateLimit(), wait);
      }
    }

    return notice;
  }

  /**
   * Formats the reset instant in the customer's own locale and timezone, dropping the date when
   * the window reopens later the same day.
   */
  private formatResetTime(resetsAt: Date): string {
    const isToday = resetsAt.toDateString() === new Date().toDateString();
    return isToday
      ? resetsAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : resetsAt.toLocaleString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit'
      });
  }

  private clearRateLimit(): void {
    this.clearRateLimitTimer();
    this.rateLimitNotice.set(null);
  }

  private clearRateLimitTimer(): void {
    if (this.rateLimitTimer) {
      clearTimeout(this.rateLimitTimer);
      this.rateLimitTimer = null;
    }
  }

  /**
   * Closes off the streaming message and releases the composer.
   */
  private finishStreamingMessage(apply: (message: AiChatMessageModel) => void): void {
    const streaming = this.messages().find((entry) => entry.is_streaming);
    if (streaming) {
      apply(streaming);
      streaming.is_streaming = false;
    }
    this.isAwaitingReply.set(false);
    this.touchMessages();
    // Every way a turn can end - completed, refused, failed, stopped - lands here, so this is
    // the one place the finished transcript needs saving.
    this.persistConversation();
  }

  /**
   * Re-emits the list after a message object was updated in place. `@for` tracks by id, so the
   * rows are re-bound rather than rebuilt - which is what keeps a 17-row table from flickering
   * once per frame.
   */
  private touchMessages(): void {
    this.messages.update((current) => [...current]);
  }

  /**
   * Writes the conversation to storage, so a reload keeps it. Called at turn boundaries rather
   * than per frame - persisting inside the stream would write once per token.
   */
  private persistConversation(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.thread || !this.messages().length) {
      this.clearStoredConversation();
      return;
    }

    const record: PersistedConversation = {
      id: this.thread.id,
      messages: this.messages()
        .slice(-AiChatConversationStore.MAX_PERSISTED_MESSAGES)
        // A half-streamed bubble must never come back as one: without this the restored message
        // keeps is_streaming/status_label and shows "Pebbl is thinking" for ever, with no turn
        // in flight to ever finish it.
        //
        // Attachment urls are stripped for a related reason: while the page is open they are
        // blob: object urls (the local preview the thumbnail was drawn from), and after a send
        // they belong to a File this tab no longer holds. Persisted, they would come back as
        // broken images on the next visit. The uuid is what survives; restoreAttachmentUrls()
        // trades it for a fresh presigned url.
        .map((entry) => ({
          ...entry,
          is_streaming: false,
          status_label: undefined,
          attachments: entry.attachments?.map((item) => ({ ...item, url: null }))
        }))
    };

    try {
      localStorage.setItem(STORAGE.AI_CHAT_CONVERSATION_KEY, JSON.stringify(record));
    } catch {
      // Full or unavailable storage (private mode, quota) must not break the conversation on
      // screen - it only costs the customer the ability to resume it later.
      this.debugLogger.debug('Pebbl conversation could not be saved');
    }
  }

  /**
   * Reads and validates the stored conversation. Anything malformed is dropped rather than
   * half-restored, so a bad record cannot wedge the surface on every load.
   */
  private readStoredConversation(): PersistedConversation | null {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(STORAGE.AI_CHAT_CONVERSATION_KEY);
    } catch {
      return null;
    }

    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as PersistedConversation;
      if (!parsed?.id || !Array.isArray(parsed.messages) || !parsed.messages.length) {
        return null;
      }
      return parsed;
    } catch {
      this.clearStoredConversation();
      return null;
    }
  }

  /**
   * Removes the stored conversation. The one thing that must clear it is Clear Chat; signing
   * out clears it too, so the next person at the browser cannot read it.
   */
  private clearStoredConversation(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.removeItem(STORAGE.AI_CHAT_CONVERSATION_KEY);
    } catch {
      // Nothing to do - the record is unreadable anyway.
    }
  }
}
